import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ScrollText, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_TYPES = [
  'all', 'register', 'login', 'login_failed', 'password_reset_requested', 'password_reset',
  'email_verified', '2fa_toggled', '2fa_failed', 'team_invite', 'team_update', 'team_remove',
  'email_settings_updated',
];

export default function AuditLogs() {
  const navigate = useNavigate();
  const [data, setData] = useState({ logs: [], total: 0, pages: 1 });
  const [action, setAction] = useState('all');
  const [page, setPage] = useState(1);
  const [settings, setSettings] = useState({ audit_log_retention_days: 180, audit_log_archive_on_startup: true });
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveMessage, setArchiveMessage] = useState('');

  const fetchLogs = useCallback(() => {
    const params = new URLSearchParams({ page, limit: 30 });
    if (action !== 'all') params.set('action', action);
    api.get(`/admin/audit-logs?${params}`).then(r => setData(r.data)).catch(() => {});
  }, [action, page]);
  useEffect(() => {
    fetchLogs();
    api.get('/admin/settings').then(r => {
      setSettings({
        audit_log_retention_days: r.data?.audit_log_retention_days ?? 180,
        audit_log_archive_on_startup: r.data?.audit_log_archive_on_startup ?? true,
        audit_log_last_archived_at: r.data?.audit_log_last_archived_at || null,
        audit_log_last_archived_count: r.data?.audit_log_last_archived_count ?? 0,
      });
    }).catch(() => {});
  }, [fetchLogs]);

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));
  const lastArchivedLabel = settings.audit_log_last_archived_at
    ? `${new Date(settings.audit_log_last_archived_at).toLocaleString()}${settings.audit_log_last_archived_count ? ` • ${settings.audit_log_last_archived_count} logs archived` : ''}`
    : 'Not archived yet';

  const saveRetention = async () => {
    setSaving(true);
    try {
      const current = await api.get('/admin/settings');
      await api.put('/admin/settings', {
        ...current.data,
        audit_log_retention_days: settings.audit_log_retention_days,
        audit_log_archive_on_startup: settings.audit_log_archive_on_startup,
        audit_log_last_archived_at: current.data?.audit_log_last_archived_at || null,
        audit_log_last_archived_count: current.data?.audit_log_last_archived_count ?? 0,
      });
      toast.success('Audit retention settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save audit retention');
    } finally {
      setSaving(false);
    }
  };

  const archiveNow = async () => {
    setArchiving(true);
    setArchiveMessage('');
    try {
      const { data } = await api.post('/admin/audit-logs/archive', {
        retention_days: settings.audit_log_retention_days ?? 180,
      });
      const archived = data?.archived ?? 0;
      const deleted = data?.deleted ?? 0;
      const cutoff = data?.cutoff || '';
      const collection = data?.collection || 'audit_logs_archive';
      if (data?.last_archived_at) {
        setSettings(prev => ({
          ...prev,
          audit_log_last_archived_at: data.last_archived_at,
          audit_log_last_archived_count: data.last_archived_count ?? deleted,
        }));
      }
      const message = `Archived ${archived} logs into ${collection}${deleted ? ` and removed ${deleted} from the live log` : ''}${cutoff ? ` (cutoff ${cutoff})` : ''}.`;
      setArchiveMessage(message);
      toast.success('Audit logs archived');
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive audit logs');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Audit Logs</h1>
          <p className="text-slate-500 text-sm mt-0.5">Track security events and admin actions</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={action} onValueChange={v => { setAction(v); setPage(1); }}>
            <SelectTrigger className="w-52" data-testid="audit-action-filter"><SelectValue placeholder="Filter by action" /></SelectTrigger>
            <SelectContent>
              {ACTION_TYPES.map(a => <SelectItem key={a} value={a}>{a === 'all' ? 'All Actions' : a.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/settings#audit-retention')} data-testid="back-to-settings-btn">
            Back to settings
          </Button>
          <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-audit-logs"><RefreshCw size={14} /></Button>
        </div>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="audit-retention-card">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Retention</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Control how long live audit logs are kept before they move into the archive collection.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Retention window (days)</Label>
              <Input
                type="number"
                min="1"
                max="3650"
                value={settings.audit_log_retention_days}
                onChange={e => update('audit_log_retention_days', Math.max(1, parseInt(e.target.value) || 180))}
                className="mt-1"
                data-testid="audit-page-retention-days"
              />
            </div>
            <div>
              <Label>Archive on startup</Label>
              <div className="mt-2 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <input
                  type="checkbox"
                  checked={!!settings.audit_log_archive_on_startup}
                  onChange={e => update('audit_log_archive_on_startup', e.target.checked)}
                  className="w-4 h-4"
                  data-testid="audit-page-archive-startup"
                />
                <span className="text-sm text-slate-700">Run archival automatically when the API starts</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={saveRetention} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-audit-retention-btn">
              {saving ? 'Saving...' : 'Save retention settings'}
            </Button>
            <Button variant="outline" onClick={archiveNow} disabled={archiving} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="archive-audit-now-btn">
              {archiving ? 'Archiving...' : 'Archive old audit logs now'}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Last archived: <span className="font-medium text-slate-700">{lastArchivedLabel}</span>
          </p>
          {archiveMessage && (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              {archiveMessage}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0 overflow-x-auto">
          {data.logs.length === 0 ? (
            <div className="py-12 text-center">
              <ScrollText size={36} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">No audit logs yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Action</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Actor</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Details</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">IP</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.logs.map((l, i) => (
                  <TableRow key={i} className="hover:bg-slate-50/50" data-testid={`audit-row-${i}`}>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${l.action?.includes('failed') ? 'bg-red-100 text-red-800' : l.action?.includes('login') ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}`}>
                        {l.action?.replace(/_/g, ' ')}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{l.actor_email || l.email || l.actor_id?.substring(0, 8) || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">
                      {l.reason || l.target_email || l.target_id || (l.enabled !== undefined ? `enabled: ${l.enabled}` : '') || '-'}
                    </TableCell>
                    <TableCell className="text-xs text-slate-400 mono-nums">{l.ip || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-sm rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-40">Previous</button>
          <span className="text-sm text-slate-500">Page {page} of {data.pages}</span>
          <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page === data.pages} className="px-3 py-1.5 text-sm rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}
